import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

export interface GrowthSprintPhase {
  id: string;
  name: string;
  month_of_quarter: number;
  order_index: number;
  brand_id: string;
  methodology: string;
  created_at: string;
  updated_at: string;
}

export const useGrowthSprintPhases = (brandId?: string, methodology: string = '2y3x') => {
  const [phases, setPhases] = useState<GrowthSprintPhase[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchPhases = async () => {
    if (!brandId) return;
    
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('growth_sprint_phases')
        .select('*')
        .eq('brand_id', brandId)
        .eq('methodology', methodology)
        .order('order_index');

      if (error) throw error;
      setPhases(data || []);
    } catch (error) {
      console.error('Error fetching phases:', error);
      toast({
        title: "Error",
        description: "Failed to load phases.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPhases();
  }, [brandId, methodology]);

  const createPhase = async (phase: {
    name: string;
    month_of_quarter: number;
    order_index: number;
    brand_id: string;
    methodology?: string;
  }) => {
    try {
      const { data, error } = await supabase
        .from('growth_sprint_phases')
        .insert({
          ...phase,
          methodology: phase.methodology || methodology
        })
        .select()
        .single();

      if (error) throw error;

      toast({
        title: "Success",
        description: "Phase created successfully."
      });

      await fetchPhases();
      return data;
    } catch (error: any) {
      console.error('Error creating phase:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create phase.",
        variant: "destructive"
      });
      return null;
    }
  };

  const updatePhase = async (phaseId: string, updates: { name?: string; order_index?: number }) => {
    try {
      const { error } = await supabase
        .from('growth_sprint_phases')
        .update(updates)
        .eq('id', phaseId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Phase updated successfully."
      });

      await fetchPhases();
      return true;
    } catch (error: any) {
      console.error('Error updating phase:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update phase.",
        variant: "destructive"
      });
      return false;
    }
  };

  const deletePhase = async (phaseId: string) => {
    try {
      // Check if phase has tasks assigned
      const { data: taskCount, error: countError } = await supabase
        .from('growth_tasks')
        .select('id', { count: 'exact', head: true })
        .eq('phase_id', phaseId);

      if (countError) throw countError;

      // @ts-ignore - count is available in head request
      if (taskCount && taskCount > 0) {
        toast({
          title: "Cannot Delete",
          description: "This phase has tasks assigned. Please reassign or delete the tasks first.",
          variant: "destructive"
        });
        return false;
      }

      const { error } = await supabase
        .from('growth_sprint_phases')
        .delete()
        .eq('id', phaseId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Phase deleted successfully."
      });

      await fetchPhases();
      return true;
    } catch (error: any) {
      console.error('Error deleting phase:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete phase.",
        variant: "destructive"
      });
      return false;
    }
  };

  const upsertDefaultPhases = async (brandIdParam: string, methodologyParam: string = '2y3x') => {
    try {
      const { error } = await supabase.rpc('upsert_default_phases_for_brand', {
        brand_uuid: brandIdParam,
        method: methodologyParam
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Default phases created successfully."
      });

      await fetchPhases();
      return true;
    } catch (error: any) {
      console.error('Error upserting default phases:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create default phases.",
        variant: "destructive"
      });
      return false;
    }
  };

  return {
    phases,
    isLoading,
    createPhase,
    updatePhase,
    deletePhase,
    upsertDefaultPhases,
    refetch: fetchPhases
  };
};
